import { useParams, Link } from 'react-router-dom';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import Breadcrumb from '@/components/feature/Breadcrumb';
import ScrollToTop from '@/components/feature/ScrollToTop';
import { articles, categories } from '@/mocks/data';

export default function ArticlePage() {
  const { id } = useParams<{ id: string }>();
  const article = articles.find((a) => a.id === id);

  if (!article) {
    return (
      <>
        <Navbar />
        <main className="min-h-[60vh] flex items-center justify-center">
          <div className="text-center">
            <h1 className="text-xl font-bold text-foreground-900 mb-2">文章未找到</h1>
            <Link to="/" className="text-sm text-primary-500 hover:text-primary-600">返回首页</Link>
          </div>
        </main>
        <Footer />
      </>
    );
  }

  const getCategoryName = (slug: string) => {
    const cat = categories.find((c) => c.slug === slug);
    return cat ? cat.name : slug;
  };

  const relatedArticles = articles
    .filter((a) => a.id !== article.id && a.category === article.category)
    .slice(0, 3);

  return (
    <>
      <ScrollToTop />
      <Navbar />
      <Breadcrumb />
      <main>
        <article className="py-10 md:py-14 bg-background-50">
          <div className="max-w-[800px] mx-auto px-4 md:px-6">
            <div className="mb-6">
              <nav className="flex items-center gap-2 text-sm flex-wrap mb-4">
                <Link to="/" className="text-foreground-500 hover:text-primary-600 transition-colors">首页</Link>
                <i className="ri-arrow-right-s-line text-foreground-400 text-xs"></i>
                <Link to={`/category/${article.category}`} className="text-foreground-500 hover:text-primary-600 transition-colors">
                  {getCategoryName(article.category)}
                </Link>
                <i className="ri-arrow-right-s-line text-foreground-400 text-xs"></i>
                <span className="text-foreground-900 font-medium">文章详情</span>
              </nav>

              <h1 className="text-2xl md:text-3xl font-heading font-bold text-foreground-900 mb-4 leading-snug">
                {article.title}
              </h1>

              <div className="flex items-center gap-4 text-sm text-foreground-500 mb-6 flex-wrap">
                <span className="flex items-center gap-1.5">
                  <i className="ri-user-line"></i>
                  {article.author}
                </span>
                <span className="flex items-center gap-1.5">
                  <i className="ri-calendar-line"></i>
                  {article.date}
                </span>
                <span className="flex items-center gap-1.5">
                  <i className="ri-time-line"></i>
                  {article.readTime} 阅读
                </span>
              </div>

              <div className="flex items-center gap-2 flex-wrap">
                {article.tags.map((tag) => (
                  <span key={tag} className="px-3 py-1 text-xs bg-accent-100 text-accent-800 rounded-full font-medium">
                    {tag}
                  </span>
                ))}
              </div>
            </div>

            <div className="relative h-64 md:h-80 rounded-lg overflow-hidden mb-8">
              <img
                src={article.imageUrl}
                alt={article.title}
                className="w-full h-full object-cover object-top"
              />
            </div>

            <div
              className="prose prose-sm md:prose-base max-w-none text-foreground-800 leading-relaxed"
              dangerouslySetInnerHTML={{ __html: article.content }}
            />

            <div className="mt-10 pt-8 border-t border-background-200/60">
              <h3 className="text-lg font-heading font-bold text-foreground-900 mb-4">相关文章</h3>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                {relatedArticles.map((related) => (
                  <Link
                    key={related.id}
                    to={`/article/${related.id}`}
                    className="block bg-background-50 border border-background-200/70 rounded-lg p-4 hover:border-primary-300 transition-all cursor-pointer group"
                  >
                    <h4 className="text-sm font-semibold text-foreground-900 group-hover:text-primary-600 transition-colors line-clamp-2 mb-2">
                      {related.title}
                    </h4>
                    <p className="text-xs text-foreground-500 line-clamp-2 mb-2">{related.excerpt}</p>
                    <span className="text-xs text-foreground-400">{related.date}</span>
                  </Link>
                ))}
              </div>
            </div>
          </div>
        </article>
      </main>
      <Footer />
      <script type="application/ld+json">
        {JSON.stringify({
          '@context': 'https://schema.org',
          '@type': 'Article',
          headline: article.title,
          description: article.excerpt,
          author: { '@type': 'Person', name: article.author },
          datePublished: article.date,
          publisher: {
            '@type': 'Organization',
            name: '皇冠体育',
          },
        })}
      </script>
    </>
  );
}